import { html } from '@effectivastudio/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import style from './style.lit.css';
import translations from './translations.js';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu in card footer
 *
 * @prop cardId - The id of a card (Generate automatically from parent card)
 * @prop type - Card type... eg NewsCard (@card.type in rails) (Generate automatically from parent card)
 * @prop contactUrl - Url for contact form
 * @prop hideValue - Is the card hidden?
 * @prop pinValue - Is the card pinned?
 * @prop contactOn - Show contact action
 * @prop pinOn - Show pin action
 * @prop editOn - Show edit/settings action
 * @prop deleteOn - Show delete action
 * @prop duplicateOn - Show duplicate action
 * @prop sendViaEmailOn - Show send via email action
 * @prop adminLinkOn - Show admin action
 * @prop hideOn - Show hide action
 * @prop moveOn - Move card action
 * @prop hideUrl - Url for hide/reveal action
 * @prop pinUrl - Url for pin/unpin action
 * @prop duplicateUrl - Url for duplicate action
 * @prop editUrl - Url for edit/settings action
 * @prop moveUrl - Url for move card action
 * @prop buttonClass - For different trigger button style (optional)
 */

export default class CardMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  /** @internal */
  static styles = [BaseMenu.styles, style];

  static properties = {
    cardId: { type: Number },
    type: { type: String },
    buttonClass: { type: String },
    hideValue: { type: Boolean },
    pinValue: { type: Boolean },
    subscribeValue: { type: Boolean },
    bookmarkValue: { type: Boolean },

    // Permissions
    contactOn: { type: Boolean },
    pinOn: { type: Boolean },
    editOn: { type: Boolean },
    deleteOn: { type: Boolean },
    duplicateOn: { type: Boolean },
    sendViaEmailOn: { type: Boolean },
    adminLinkOn: { type: Boolean },
    hideOn: { type: Boolean },
    moveOn: { type: Boolean },

    // URLs
    contactUrl: { type: String },
    hideUrl: { type: String },
    pinUrl: { type: String },
    duplicateUrl: { type: String },
    editUrl: { type: String },
    moveUrl: { type: String },

    // States
    card: { state: true },
    cardTitle: { state: true },
  };

  constructor() {
    super();

    /** @internal */
    this.currentBrowserHref = window.location.href;
  }

  connectedCallback() {
    super.connectedCallback();
    this.card =
      document.querySelector(`bu-card[cardId="${this.cardId}"]`) ||
      document.querySelector(`bu-card-event-line-1[cardId="${this.cardId}"]`);
    if (this.card) this.card.menu = this;
  }

  willUpdate(changedProperties) {
    for (const [key, value] of changedProperties.entries()) {
      // Update properties on parent card (only watched)
      if (this.card?.constructor.watchedProps?.includes(key)) {
        const sequentialUpdate = value !== undefined;
        if (sequentialUpdate) {
          this.card[key] = this[key];
        }
      }
    }
  }

  /** @internal */
  get instanceId() {
    return this.cardId;
  }

  /** @internal */
  get urlType() {
    const type = this.type?.replace('Card', '').toLowerCase();

    return `${type}_cards`;
  }

  /** @internal */
  get baseUrl() {
    return `/${this.urlType}/${this.cardId}`;
  }

  /** @internal */
  get reportUrl() {
    return `/complaints/new?complaint[complainable_id]=${this.cardId}&complaint[complainable_type]=Card`;
  }

  /** @internal */
  get sendViaEmailUrl() {
    return `/cards/${this.cardId}/send_via_email/new`;
  }

  /** @internal */
  get likesUrl() {
    return `/cards/${this.cardId}/likes`;
  }

  /** @internal */
  get subscribeItem() {
    return {
      title: this.textBaseOnActiveKey('actions.subscribe', 'subscribeValue'),
      action: this.actionFor('toggleSubscribe'),
      icon: this.subscribeValue ? 'bell-s' : 'bell',
      partName: `subscribe ${this.subscribeValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get bookmarkItem() {
    return {
      title: this.textBaseOnActiveKey('actions.bookmark', 'bookmarkValue'),
      action: this.actionFor('toggleBookmark'),
      icon: this.bookmarkValue ? 'bookmark-s' : 'bookmark',
      partName: `bookmark ${this.bookmarkValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get pinItem() {
    return {
      title: this.textBaseOnActiveKey('actions.pin', 'pinValue'),
      action: this.actionFor('togglePin'),
      icon: this.pinValue ? 'thumbtack-s' : 'thumbtack',
      partName: `pin ${this.pinValue ? 'active' : ''}`,
    };
  }

  /** @internal */
  get likesItem() {
    return {
      title: this.t('actions.likes'),
      action: this.actionFor('cardGet', [this.likesUrl]),
      icon: 'heart',
      partName: 'likes',
    };
  }

  /** @internal */
  get contactItem() {
    return {
      title: this.t('actions.contact'),
      action: this.actionFor('cardContact', [this.contactUrl]),
      icon: 'comment-alt-lines',
      partName: 'contact',
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.inappropriate_report'),
      action: this.actionFor('cardGet', [this.reportUrl]),
      icon: 'exclamation-triangle',
      partName: 'report',
    };
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t('actions.settings'),
      action: this.actionFor('cardGet', [this.editUrl]),
      icon: 'cog',
      partName: 'edit',
    };
  }

  /** @internal */
  get duplicateItem() {
    return {
      title: this.t('actions.duplicate'),
      action: this.actionFor('cardGet', [this.duplicateUrl]),
      icon: 'copy',
      partName: 'duplicate',
    };
  }

  /** @internal */
  get sendViaEmailItem() {
    return {
      title: this.t('actions.send_via_email'),
      action: this.actionFor('cardGet', [this.sendViaEmailUrl]),
      icon: 'envelope',
      partName: 'send-via-email',
    };
  }

  /** @internal */
  get hideItem() {
    return {
      title: this.hideValue ? this.t('actions.reveal') : this.t('actions.hide'),
      action: this.actionFor('cardGet', [this.hideUrl]),
      icon: this.hideValue ? 'eye-s' : 'eye',
      partName: 'hide-item',
    };
  }

  /** @internal */
  get moveItem() {
    return {
      title: this.t('actions.move'),
      action: this.actionFor('cardGet', [this.moveUrl]),
      icon: 'sign-out-alt',
      partName: 'move',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('cardDelete'),
      role: 'destructive',
      icon: 'delete',
      partName: 'delete destructive',
    };
  }

  /** @internal */
  get adminLinkItem() {
    return {
      title: this.t('actions.admin'),
      action: this.actionFor('cardVisit', [
        `/admin/${this.urlType}/${this.cardId}`,
      ]),
      icon: 'shield-check',
      partName: 'admin',
    };
  }

  /** @internal */
  get items() {
    return [
      this.likesItem,
      this.subscribeItem,
      this.bookmarkItem,
      this.pinOn && this.pinItem,
      this.contactOn && this.contactItem,
      this.reportItem,
      this.editOn && this.settingsItem,
      this.duplicateOn && this.duplicateItem,
      this.sendViaEmailOn && this.sendViaEmailItem,
      this.hideOn && this.hideItem,
      this.moveOn && this.moveItem,
      (this.deleteOn || this.adminLinkOn) && this.divider,
      this.deleteOn && this.deleteItem,
      this.adminLinkOn && this.adminLinkItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.cardTitle,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-card-menu',
      id: this.instanceId,
      method: method,
      args: args,
    };
  }

  urlFor(action) {
    return `${this.baseUrl}/${action}`;
  }

  activeKey(prop) {
    return this[prop] ? 'on' : 'off';
  }

  textBaseOnActiveKey(key, prop) {
    return this.t(`${key}.${this.activeKey(prop)}`);
  }

  toggleSubscribe() {
    this.cardToggle({
      url: `/cards/${this.cardId}/toggle_subscribe`,
      prop: 'subscribeValue',
      key: 'snack.subscribe',
    });
  }

  toggleBookmark() {
    this.cardToggle({
      url: `/cards/${this.cardId}/toggle_bookmark`,
      prop: 'bookmarkValue',
      key: 'snack.bookmark',
    });
  }

  togglePin() {
    this.cardToggle({
      url: this.pinUrl,
      prop: 'pinValue',
      key: 'snack.pin',
    });
  }

  async cardToggle({ url, prop, key }) {
    try {
      await this.commonActions.patch(url, {
        headers: {
          accept: 'application/javascript',
        },
        body: JSON.stringify({
          // Use ? as in JS tests we don't have pusher
          socket_id: window.App?.pusher?.connection?.socket_id,
        }),
      });
      this[prop] = !this[prop];
      this.commonActions.displaySnackBarSuccess(
        this.textBaseOnActiveKey(key, prop)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async cardContact(url) {
    try {
      await this.commonActions.post(url, {
        headers: {
          accept: 'application/javascript',
        },
      });
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  cardVisit(url) {
    this.commonActions.visit(url);
  }

  async cardGet(url) {
    try {
      await this.commonActions.get(url);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async cardDelete() {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }
    try {
      const res = await this.commonActions.delete(`${this.baseUrl}?trash=true`);
      const redirectUrl = res.headers.get('X-Xhr-Redirect');
      const isFullView = this.currentBrowserHref.includes(this.baseUrl);

      if (isFullView) {
        if (redirectUrl) {
          this.commonActions.visit(redirectUrl);
        } else {
          document.querySelector('.js-back-or-default').click();
        }
        return false;
      }

      this.card.remove();

      this.commonActions.displaySnackBarSuccess(
        this.t('snack.card_delete_success')
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html`
      <ef-dot-menu
        .config=${this.dotMenuConfig}
        buttonClass=${ifDefined(this.buttonClass)}>
      </ef-dot-menu>
    `;
  }
}

CardMenu.registerTagName('bu-card-menu');
CardMenu.registerCommonActions('bu-card-menu');

customElements.get('bu-card-menu') ||
  customElements.define('bu-card-menu', CardMenu);
